import * as React from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useIntl } from '@leagueplatform/locales';
import { Box, UtilityText } from '@leagueplatform/genesis-core';
import { TableLoadingState } from '../table-loading-state/table-loading-state.component';
import {
  TableExpandColumnHeader,
  TableExpandColumnCell,
  TableExpandedRow,
} from './table-expand-column.component';

export type AuditLogTableProps<TData> = {
  columns: ColumnDef<TData>[];
  data: TData[];
  isLoading?: boolean;
  renderExpandedContent?: (row: TData) => React.ReactNode;
};

export type AuditLogTableRef = {
  resetExpanded: () => void;
};

function AuditLogTableInner<TData>(
  {
    columns,
    data,
    isLoading,
    renderExpandedContent,
  }: AuditLogTableProps<TData>,
  ref: React.Ref<AuditLogTableRef>,
) {
  const { formatMessage } = useIntl();

  const renderExpandHeader = React.useCallback(
    () => (
      <TableExpandColumnHeader
        expandHeaderLabel={formatMessage({
          id: 'AUDIT_LOG_EXPAND_VERSION_DATA',
        })}
      />
    ),
    [formatMessage],
  );

  const renderExpandCell = React.useCallback(
    (context: any) => (
      <TableExpandColumnCell
        row={context.row}
        expandButtonLabel={formatMessage({ id: 'AUDIT_LOG_EXPAND_ROW_DATA' })}
        collapseButtonLabel={formatMessage({
          id: 'AUDIT_LOG_COLLAPSE_ROW_DATA',
        })}
      />
    ),
    [formatMessage],
  );

  const expandColumn: ColumnDef<TData> = React.useMemo(
    () => ({
      id: 'expand',
      header: renderExpandHeader,
      cell: renderExpandCell,
    }),
    [renderExpandHeader, renderExpandCell],
  );

  // Combine expand column with existing columns - put expand column last
  // Only add expand column if renderExpandedContent is provided
  const allColumns = React.useMemo(
    () => (renderExpandedContent ? [...columns, expandColumn] : columns),
    [columns, expandColumn, renderExpandedContent],
  );

  const table = useReactTable({
    data,
    columns: allColumns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    // row can expand if renderExpandedContent is provided
    getRowCanExpand: () => Boolean(renderExpandedContent),
  });

  React.useImperativeHandle(
    ref,
    () => ({
      resetExpanded: () => {
        table.setExpanded({});
      },
    }),
    [table],
  );

  const renderTableBody = () => {
    if (isLoading) {
      return (
        <TableLoadingState
          colSpan={allColumns.length}
          numberOfRows={3}
          header
          marginBottom="one"
          testId="SkeletonTableGroup"
        />
      );
    }

    return table.getRowModel().rows.map((row, rowIndex) => (
      <React.Fragment key={row.id}>
        {/* Normal row UI */}
        <Box as="tr" key={row.id}>
          {row.getVisibleCells().map((cell, cellIndex) => {
            const isLastRow = rowIndex === table.getRowModel().rows.length - 1;
            const isExpandColumn = cell.column.id === 'expand';
            const isFirstCell = cellIndex === 0;
            const isLastCell = cellIndex === row.getVisibleCells().length - 1;
            const isLastRowWithoutExpansion = isLastRow && !row.getIsExpanded();

            return (
              <Box
                as="td"
                key={cell.id}
                css={{
                  padding: isExpandColumn ? '0' : '$one',
                  borderBottom: isLastRowWithoutExpansion
                    ? 'none'
                    : '1px solid $onSurfaceBorderSubdued',
                  // Apply border radius to first and last cells in the last row
                  ...(isLastRowWithoutExpansion &&
                    isFirstCell && {
                      borderBottomLeftRadius: '$medium',
                    }),
                  ...(isLastRowWithoutExpansion &&
                    isLastCell && {
                      borderBottomRightRadius: '$medium',
                    }),
                }}
              >
                {/* Check if this is the expand column */}
                {cell.column.id === 'expand' ? (
                  flexRender(cell.column.columnDef.cell, cell.getContext())
                ) : (
                  <UtilityText size="sm">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </UtilityText>
                )}
              </Box>
            );
          })}
        </Box>
        {row.getIsExpanded() && renderExpandedContent && (
          <TableExpandedRow
            row={row}
            colSpan={row.getAllCells().length}
            renderExpandedContent={renderExpandedContent}
          />
        )}
      </React.Fragment>
    ));
  };

  return (
    <Box
      as="table"
      css={{
        textAlign: 'left',
        width: '100%',
        borderColor: '$onSurfaceBorderSubdued',
        borderWidth: '$thin',
        borderStyle: 'solid',
        borderRadius: '$medium',
        borderSpacing: 0,
      }}
    >
      <Box as="thead">
        {table.getHeaderGroups().map((headerGroup) => (
          <Box
            as="tr"
            key={headerGroup.id}
            css={{ position: 'sticky', insetBlockStart: 0 }}
          >
            {headerGroup.headers.map((header, headerIndex) => {
              const isFirstHeader = headerIndex === 0;
              const isLastHeader =
                headerIndex === headerGroup.headers.length - 1;

              return (
                <Box
                  as="th"
                  key={header.id}
                  css={{
                    background: '$surfaceBackgroundTertiary',
                    padding: '$one',
                    ...(header.column.id === 'expand' && {
                      width: '$two',
                    }),
                    // Apply border radius to first and last header cells
                    ...(isFirstHeader && {
                      borderTopLeftRadius: '$medium',
                    }),
                    ...(isLastHeader && {
                      borderTopRightRadius: '$medium',
                    }),
                  }}
                >
                  {header.isPlaceholder ? null : (
                    <UtilityText emphasis="emphasized" size="sm">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </UtilityText>
                  )}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
      <Box as="tbody">{renderTableBody()}</Box>
    </Box>
  );
}

export const AuditLogTable = React.forwardRef(AuditLogTableInner) as <TData>(
  props: React.PropsWithoutRef<AuditLogTableProps<TData>> &
    React.RefAttributes<AuditLogTableRef>,
) => JSX.Element;
